import { cn } from "@/lib/utils";
import { useEffect, useRef, type ReactNode } from "react";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Modal built on the native `<dialog>` element: focus trapping, Escape handling
 * and the backdrop come from the platform, so no extra dependency is needed.
 */
export const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
}: DialogProps) => {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      className={cn(
        // `m-auto` is required: Tailwind preflight resets `margin: 0`, which defeats
        // the UA stylesheet's `dialog:modal { margin: auto; inset: 0 }` centering and
        // leaves modals pinned to the top-left corner.
        "fixed inset-0 m-auto h-fit max-h-[85vh] w-[min(92vw,32rem)] rounded-card border border-border bg-card p-0 text-card-foreground backdrop:bg-black/50",
        className,
      )}
    >
      <div className="flex flex-col gap-1 border-b border-border p-5">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      {footer ? (
        <div className="flex items-center justify-end gap-2 border-t border-border p-4">{footer}</div>
      ) : null}
    </dialog>
  );
};
